import { useEffect, useMemo, useState } from "react";
import { useCreatePosition } from "./usePositionMutations";
import { useFxRates } from "./useFxRates";
import { useBondScreenInfo } from "../bond/useBondScreenInfo";
import { useBondSchedule } from "../bond/useBondSchedule";
import { buildLifeline } from "../bond/lifelineData";
import { Shimmer } from "../../ui/loaders";
import { fmtDate, fmtNum, fmtPct, fmtRub } from "../../lib/format";

const TAX_OPTIONS = [
  { value: "resident", label: "Резидент 13 %" },
  { value: "non-resident", label: "Нерезидент 30 %" },
  { value: "iis-b", label: "ИИС-Б · без налога" },
];

// Direction B: бумага уже выбрана (секция «Покупаем» в шапке), вводим только
// параметры сделки. Слева — компактная форма, справа — paper-2 превью с
// расчётом списания, снимком cashflow и слотом для big-net-YTM при цене
// (см. ROADMAP — endpoint /api/bond/{secid}/net_ytm).
export function AddPositionModal({
  secid,
  onClose,
}: {
  secid: string;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [quantity, setQuantity] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [buyAccrued, setBuyAccrued] = useState("0");
  const [buyDate, setBuyDate] = useState(today);
  const [buyFx, setBuyFx] = useState("");
  const [tax, setTax] = useState("resident");
  const [note, setNote] = useState("");

  const screenInfo = useBondScreenInfo(secid);
  const schedule = useBondSchedule(secid);

  const bond = screenInfo.bond;
  const ccy = bond?.currency ?? "RUB";

  // Префилл курса ЦБ для не-RUB бумаги — сегодняшний spot. Истории в fx_rates
  // не накапливаем, для старой даты пользователь правит вручную.
  const fxRatesQuery = useFxRates();
  useEffect(() => {
    if (ccy === "RUB" || buyFx !== "") return;
    const rate = fxRatesQuery.data?.[ccy];
    if (rate != null) setBuyFx(rate.toString());
  }, [ccy, buyFx, fxRatesQuery.data]);
  const lifeline = useMemo(
    () => (schedule.data ? buildLifeline(schedule.data) : null),
    [schedule.data],
  );

  const qty = parseInt(quantity, 10) || 0;
  const price = parseFloat(buyPrice) || 0;
  const accrued = parseFloat(buyAccrued || "0") || 0;
  const clean = qty * price;
  const total = clean + accrued;

  const futureCoupons = lifeline ? lifeline.summary.futureCoupons * qty : 0;
  const faceRedemption = lifeline ? lifeline.summary.faceRedemption * qty : 0;
  const horizonIso = lifeline?.summary.maturityIso ?? null;
  const futureCount = lifeline?.summary.futureCount ?? 0;

  const mutation = useCreatePosition();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(
      {
        secid,
        quantity: qty,
        buy_price: price,
        buy_accrued: accrued,
        buy_date: buyDate,
        buy_fx_rate: buyFx ? parseFloat(buyFx) : null,
        tax_preset: tax,
        note: note || null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal-split" role="dialog" aria-modal="true">
        <div className="modal-body">
          <form className="modal-grid" onSubmit={submit}>
            <div className="left">
              <h2>Добавить позицию</h2>

              <BondContext
                secid={secid}
                name={bond?.secname ?? bond?.short_name ?? null}
                list_level={bond?.list_level ?? null}
                price={bond?.last_price ?? null}
              />

              <div className="trade-form">
                <div className="fields">
                  <label>
                    Кол-во
                    <input
                      type="number"
                      required
                      min={1}
                      step={1}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      autoFocus
                    />
                  </label>
                  <label>
                    Цена clean (₽ за бумагу)
                    <input
                      type="number"
                      required
                      step={0.01}
                      value={buyPrice}
                      onChange={(e) => setBuyPrice(e.target.value)}
                    />
                  </label>
                  <label>
                    НКД на покупке всего (₽)
                    <input
                      type="number"
                      step={0.01}
                      value={buyAccrued}
                      onChange={(e) => setBuyAccrued(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата покупки
                    <input
                      type="date"
                      required
                      value={buyDate}
                      onChange={(e) => setBuyDate(e.target.value)}
                    />
                  </label>
                  <label>
                    Налог
                    <select value={tax} onChange={(e) => setTax(e.target.value)}>
                      {TAX_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Курс на дату (для не-RUB)
                    <input
                      type="number"
                      step={0.0001}
                      placeholder="оставь пустым для RUB"
                      value={buyFx}
                      onChange={(e) => setBuyFx(e.target.value)}
                    />
                  </label>
                </div>
                <label>
                  Заметка
                  <textarea
                    placeholder="опционально"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="right">
              <Section title="Расчёт по форме">
                <div className="preview-row">
                  <span className="soft">Сумма clean</span>
                  <span className="mono">{fmtRub(clean)}</span>
                </div>
                <div className="preview-row">
                  <span className="soft">+ НКД</span>
                  <span className="mono">{fmtRub(accrued)}</span>
                </div>
                <div className="preview-row total">
                  <span>К списанию</span>
                  <span className="mono">{fmtRub(total)}</span>
                </div>
              </Section>

              <Section title="Доходность">
                {bond && bond.net_ytm != null ? (
                  <>
                    <div
                      className="big-num accent"
                      style={{ fontSize: 28, marginTop: 2 }}
                    >
                      {fmtPct(bond.net_ytm)}
                    </div>
                    <div className="soft" style={{ fontSize: 11.5, marginTop: 2 }}>
                      net по цене скрина {fmtNum(bond.last_price)} ₽
                    </div>
                    {price > 0 && Math.abs(price - bond.last_price) > 0.5 && (
                      <div className="soft" style={{ fontSize: 11.5, marginTop: 2 }}>
                        твоя цена {fmtNum(price)} ₽ — точный пересчёт net появится позже
                      </div>
                    )}
                  </>
                ) : screenInfo.isPending ? (
                  <>
                    <Shimmer width={120} height={28} />
                    <div style={{ marginTop: 6 }}>
                      <Shimmer width={160} height={11} />
                    </div>
                  </>
                ) : (
                  <div className="soft" style={{ fontSize: 12 }}>
                    откроется, когда подтянется скрин
                  </div>
                )}
              </Section>

              <Section title="Прогноз cashflow">
                {lifeline && futureCount > 0 ? (
                  <div className="soft" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                    {futureCount} событ. ·{" "}
                    <b className="ink">≈ {fmtRub(futureCoupons)}</b> купонов
                    {faceRedemption > 0 && (
                      <>
                        <br />
                        + {fmtRub(faceRedemption)} возврат тела
                      </>
                    )}
                    {horizonIso && (
                      <>
                        <br />
                        горизонт{" "}
                        <span className="mono ink">{fmtDate(horizonIso)}</span>
                      </>
                    )}
                  </div>
                ) : schedule.isPending ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <Shimmer width={200} height={12} />
                    <Shimmer width={160} height={12} />
                    <Shimmer width={140} height={12} />
                  </div>
                ) : (
                  <div className="soft" style={{ fontSize: 12 }}>
                    расписания нет
                  </div>
                )}
              </Section>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginTop: "auto",
                }}
              >
                <button
                  type="submit"
                  className="btn primary"
                  disabled={mutation.isPending}
                  style={{ width: "100%" }}
                >
                  {mutation.isPending ? "сохраняю…" : "Создать позицию"}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={onClose}
                  style={{ width: "100%" }}
                >
                  Отмена
                </button>
                {mutation.isError && (
                  <span
                    className="status error"
                    style={{ fontSize: 11.5, color: "var(--bad)" }}
                  >
                    ошибка: {String(mutation.error)}
                  </span>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="label" style={{ marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function BondContext({
  secid,
  name,
  list_level,
  price,
}: {
  secid: string;
  name: string | null;
  list_level: number | null;
  price: number | null;
}) {
  return (
    <div className="bond-context">
      <div style={{ minWidth: 0 }}>
        <div className="ctx-label">Покупаем</div>
        <div className="row" style={{ gap: 8, marginTop: 2, minWidth: 0 }}>
          <span
            className="ctx-name"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 320,
            }}
          >
            {name ?? <Shimmer width={200} height={14} />}
          </span>
          {list_level === 3 && <span className="tag-l3">L3</span>}
          <span className="mono soft" style={{ fontSize: 11 }}>{secid}</span>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div className="ctx-label">Цена скрина</div>
        <div className="mono" style={{ fontSize: 13 }}>
          {price == null ? <Shimmer width={80} height={13} /> : `${fmtNum(price)} ₽`}
        </div>
      </div>
    </div>
  );
}
